import api from 'api';
import { sampleRandomColorFromPalette } from 'constants/colorPalette';
import { errorMessageFactory } from 'factories/api';
import type { Action } from 'redux';
import { manipulateProjectVariant } from 'redux/modules/projects.reducer';
import { archiveProjectsForClient } from 'redux/modules/projects.types';
import { showToast } from 'redux/modules/toasts.actions';
import { getClientEntities } from 'redux/selectors/clients';
import { getProjectById, readProjectIds } from 'redux/selectors/projects';
import { extractProjectBase } from 'redux/selectors/utils/projects';
import { TrackingActions } from 'tracking/actions';
import type { Client, ClientDraft } from 'types/client';
import type { Thunk } from 'types/redux';
import { fetchPaginated } from 'utils/api';
import { actions } from './clients';

export function fetchClients(options: { scope?: 'global' } = {}): Thunk<Promise<void>> {
  return dispatch => {
    const perPage = 1_000;
    return fetchPaginated<Client>({ perPage }, page =>
      api.getClients({ page, perPage, scope: options.scope })
    ).then(clients => {
      dispatch(actions.fetchedClients(clients));
    });
  };
}

/**
 * Useful to refresh project counts after projects are created/removed/archived/restored
 * @param id Client id
 */
export function fetchClient(id: number): Thunk<Promise<void>> {
  return async dispatch => {
    const client = await api.getClient(id);
    dispatch(actions.fetchedClient({ client }));
  };
}

export function saveClient(payload: ClientDraft): Thunk<Promise<Client>> {
  return async dispatch => {
    try {
      const client = await api.saveClient(payload);
      dispatch(actions.savedClient({ client, createdEntry: !payload.id }));
      dispatch(fetchClient(client.id));
      return client;
    } catch (error) {
      dispatch(
        showToast({
          message: `Failed to update client – ${errorMessageFactory(error)}`,
          type: 'danger',
        })
      );
      throw error;
    }
  };
}

export function activateClientId(clientId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const client = getClientEntities(state)[clientId];
    return dispatch(saveClient({ ...client, active: true }));
  };
}

export function archiveClientId(clientId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const client = getClientEntities(state)[clientId];
    const projectIds = readProjectIds(state);
    const projectById = getProjectById(state);
    const projects = projectIds.reduce(
      (byId, projectId) => {
        const variant = projectById[projectId];
        if (!variant) {
          return byId;
        }
        const client = extractProjectBase(variant).client;
        if (client.id === clientId) {
          byId[projectId] = manipulateProjectVariant(
            variant,
            light => ({ ...light, active: false }),
            full => ({ ...full, active: false })
          );
        }
        return byId;
      },
      { ...projectById }
    );
    // Archive all the client's projects (backend does this as well, but we don't get notified atm)
    dispatch(archiveProjectsForClient(projects));

    return dispatch(saveClient({ ...client, active: false }));
  };
}

export function toggleArchivedOnClientId(clientId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const currentClient = getClientEntities(state)[clientId];

    if (currentClient.active) {
      return dispatch(archiveClientId(currentClient.id));
    }

    return dispatch(activateClientId(currentClient.id));
  };
}

export const search = actions.searchClients;
export const setFilter = actions.filterClients;
export const reset = () => actions.resetClients();

export function initiateDraft(draft?: Partial<ClientDraft> | null): Action {
  // Keep track of how many times the user starts creation process:
  TrackingActions.initiateUserAction('New Client');

  return actions.initiateClientDraft({
    name: '',
    color: sampleRandomColorFromPalette(),
    ...draft,
  });
}

export const updateDraft = actions.updateClientDraft;
export const clearDraft = () => actions.clearClientDraft();
